import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useQueryParams } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    DefaultPagedResponse,
    Entity,
    ShippingMethod,
    IUseQueryPaged,
    PagedResponse,
    QueryHookParams,
    QueryType,
} from "../models";
import { Operation } from "rfc6902";

export const useShippingMethodQuery = (params?: QueryHookParams): IUseQueryPaged<ShippingMethod> => {
    const shippingMethodId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { isAuthorized } = useAuth();
    const { get, post, patch, del } = useAxios();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.ShippingMethods);
    const url = Endpoints.ShippingMethods;
    const queryType = getQueryType(shippingMethodId, params?.queryType);

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<ShippingMethod>>(getRequestOptions(url, config, queryParams, sortParams, pageParams)),
        queryKey: queryKeys.shippingMethods.pagedList({ ...queryParams, ...pageParams, ...sortParams }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized && queryType === QueryType.PagedList,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<ShippingMethod>>(
                getRequestOptions(url, config, undefined, sortParams, undefined, false, true)
            ).then((response) => response.items),
        queryKey: queryKeys.shippingMethods.list({ ...sortParams }),
        placeholderData: [],
        enabled: isAuthorized && queryType === QueryType.List,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<ShippingMethod>({ url: `${url}/${shippingMethodId}`, config }),
        queryKey: queryKeys.shippingMethods.detail(shippingMethodId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === QueryType.Detail && !!params?.id,
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: Partial<ShippingMethod>) => post<Partial<ShippingMethod>, ShippingMethod>({ url, data }),
        queryKey: queryKeys.shippingMethods.all(),
        keysToInvalidate: [queryKeys.shippingMethods.lists(), queryKeys.shippingMethods.pagedLists()],
        successToast: "Shipping method created",
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) =>
            patch<Operation[], ShippingMethod>({ url: `${url}/${shippingMethodId}`, data }),
        queryKey: queryKeys.shippingMethods.detail(shippingMethodId),
        keysToInvalidate: [queryKeys.shippingMethods.lists(), queryKeys.shippingMethods.pagedLists()],
        successToast: "Shipping method updated",
    });

    const deleteMutation = useGenericMutation({
        mutationFn: () => del({ url: `${url}/${shippingMethodId}` }),
        queryKey: queryKeys.shippingMethods.detail(shippingMethodId),
        keysToInvalidate: [queryKeys.shippingMethods.lists(), queryKeys.shippingMethods.pagedLists()],
        successToast: "Shipping method deleted",
    });

    return useMemo<IUseQueryPaged<ShippingMethod>>(
        () => ({
            pagedListQuery,
            listQuery,
            detailQuery,
            createMutation,
            updateMutation,
            deleteMutation,
        }),
        [pagedListQuery, listQuery, detailQuery, createMutation, updateMutation, deleteMutation]
    );
};
